import { useEffect, useRef } from "react"
import { Icon } from './Icon'

export interface MediaLightboxItem {
  url: string
  type: 'image' | 'video' | 'pdf'
  /**
   * Burnt-in label drawn over the top of the media, as the mobile app stamps a
   * trail-camera photo. Omit it and the media is shown bare, which is what a
   * document wants — a file the reader picked by name needs no caption.
   */
  caption?: string
}

export interface MediaLightboxLabels {
  title: string
  close: string
  previous: string
  next: string
  /** Only rendered for a pdf item; omit when no item can be a pdf. */
  openInNewTab?: string
}

/**
 * Full-screen preview overlay: navigate prev/next (buttons or ← / → keys), Esc
 * or backdrop to close.
 *
 * Labels arrive resolved rather than looked up here, because the same overlay
 * serves the photo gallery and the documents tab, and "Previous photo" is the
 * wrong words for a document. The caller translates in its own namespace and
 * passes what fits.
 */
export function MediaLightbox({
  items,
  index,
  labels,
  onClose,
  onNavigate,
  onItemError,
}: {
  items: MediaLightboxItem[]
  index: number
  labels: MediaLightboxLabels
  onClose: () => void
  onNavigate: (i: number) => void
  /**
   * The item at this index could not be loaded. Worth reporting rather than
   * leaving a broken box on screen: a resolved URL can still be refused when
   * it is finally fetched, so a load failure is not the same event as a failed
   * resolve and nothing upstream has heard about it.
   */
  onItemError?: (index: number) => void
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && index > 0) onNavigate(index - 1);
      else if (e.key === "ArrowRight" && index < items.length - 1) onNavigate(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length, onClose, onNavigate]);

  const item = items[index];
  if (!item) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={labels.title}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <button
        ref={closeRef}
        className="wh-btn-icon"
        aria-label={labels.close}
        onClick={onClose}
        style={{ position: "absolute", top: 16, right: 16, color: "#fff" }}
      >
        <Icon name="close" size={24} />
      </button>

      {hasPrev && (
        <button
          className="wh-btn-icon"
          aria-label={labels.previous}
          onClick={(e) => { e.stopPropagation(); onNavigate(index - 1); }}
          style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "#fff" }}
        >
          <Icon name="chevron-left" size={28} />
        </button>
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}
      >
        {/* The caption sits over the media rather than under it, so it travels
            with a screenshot of the photo the way the app's stamp does. The
            wrapper is only as big as the media inside it — `lineHeight: 0`
            keeps the inline media from adding a text baseline gap below,
            which would leave the caption floating above the picture. */}
        <div style={{ position: "relative", display: "flex", lineHeight: 0, maxWidth: "90vw" }}>
          {item.type === "video" ? (
            <video src={item.url} controls style={{ maxWidth: "90vw", maxHeight: "80vh" }} />
          ) : item.type === "pdf" ? (
            <iframe
              src={item.url}
              title={labels.title}
              style={{ width: "90vw", height: "80vh", border: "none", borderRadius: 8, background: "#fff" }}
            />
          ) : (
            <img
              src={item.url}
              alt=""
              onError={() => onItemError?.(index)}
              style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", borderRadius: 8 }}
            />
          )}
          {item.caption && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                padding: "8px 12px",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 1,
                lineHeight: 1.4,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                // A trail camera fires at dawn over pale gravel as readily as
                // into dark forest, so the label cannot rely on the photo
                // behind it being dark. The scrim fades out instead of ending
                // on a hard edge, which would read as part of the picture.
                background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0))",
                pointerEvents: "none",
              }}
            >
              {item.caption}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, color: "#fff", fontSize: 13, opacity: 0.9 }}>
          <span>{`${index + 1} / ${items.length}`}</span>
          {/* An escape hatch, not decoration: a browser that cannot render a
              PDF in a frame shows an empty box with no hint of what to do. */}
          {item.type === "pdf" && labels.openInNewTab && (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "underline" }}
            >
              {labels.openInNewTab}
            </a>
          )}
        </div>
      </div>

      {hasNext && (
        <button
          className="wh-btn-icon"
          aria-label={labels.next}
          onClick={(e) => { e.stopPropagation(); onNavigate(index + 1); }}
          style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", color: "#fff" }}
        >
          <Icon name="chevron-right" size={28} />
        </button>
      )}
    </div>
  );
}
